import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppLocalization } from '@kaltura-ng/mc-shared';
import { KalturaRecordStatus } from 'kaltura-ngx-client';
import { AreaBlockerMessage } from '@kaltura-ng/kaltura-ui';
import { BrowserService } from 'app-shared/kmc-shell';
import { cancelOnDestroy, tag } from '@kaltura-ng/kaltura-common';
import { KalturaLive } from './kaltura-live-stream.interface';
import { KalturaLiveStreamService } from './kaltura-live-stream.service';

@Component({
  selector: 'kKalturaLiveStream',
  templateUrl: './kaltura-live-stream.component.html',
  styleUrls: ['./kaltura-live-stream.component.scss'],
  providers: [KalturaLiveStreamService],
})
export class KalturaLiveStreamComponent implements OnInit, OnDestroy {
  public _form: FormGroup;

  public _availableTranscodingProfiles: Array<{ value: number; label: string }>;

  public _enableRecordingOptions: Array<{ value: KalturaRecordStatus; label: string }>;

  public _blockerMessage: AreaBlockerMessage = null;

  public _isBusy = false;

  @Input()
  data: KalturaLive;

  @Output()
  dataChange = new EventEmitter<KalturaLive>();

  constructor(
    private _appLocalization: AppLocalization,
    private _fb: FormBuilder,
    private _kalturaLiveStreamService: KalturaLiveStreamService,
    private _browserService: BrowserService,
  ) {}

  ngOnInit(): void {
    this._createForm();
    this._fillEnableRecordingOptions();
    this._loadTranscodingProfiles();
  }

  ngOnDestroy(): void {}

  public validate(): boolean {
    if (!this._form.valid) {
      this.markFormFieldsAsTouched();
    }
    return this._form.valid;
  }

  public isFormDirty(): boolean {
    return this._form.dirty;
  }

  private _loadTranscodingProfiles(): void {
    this._updateAreaBlockerState(true, null);
    this._kalturaLiveStreamService
      .getKalturaConversionProfiles()
      .pipe(cancelOnDestroy(this))
      .subscribe(
        (transcodingProfilesList) => {
          this._availableTranscodingProfiles = transcodingProfilesList.map((transcodingProfile) => ({
            value: transcodingProfile.id,
            label: transcodingProfile.name,
          }));

          this.data.transcodingProfile = this._getSelectedTranscodingProfile(transcodingProfilesList);

          this._form.reset(this.data);
          this._toggleRecordingSelectedOption(this.data.enableRecording);
          this._updateAreaBlockerState(false, null);
        },
        (error) => {
          this._updateAreaBlockerState(false, error.message);
        },
      );
  }

  private _getSelectedTranscodingProfile(transcodingProfilesList): number {
    if (!transcodingProfilesList || !transcodingProfilesList.length) {
      return null;
    }

    const profileIdFromCache = this._browserService.getFromLocalStorage('kalturaStreamType.selectedTranscodingProfile');
    const profileExistsInList = transcodingProfilesList.findIndex((profile) => profile.id === profileIdFromCache) > -1;

    // if selected profile id exists in the list return it ; else return first option
    if (profileIdFromCache && profileExistsInList) {
      return profileIdFromCache;
    }
    this._browserService.setInLocalStorage(
      'kalturaStreamType.selectedTranscodingProfile',
      transcodingProfilesList[0].id,
    );
    return transcodingProfilesList[0].id;
  }

  private _fillEnableRecordingOptions() {
    this._enableRecordingOptions = [
      {
        value: KalturaRecordStatus.perSession,
        label: this._appLocalization.get(
          'applications.upload.prepareLive.kalturaStreamType.enableRecordingOptions.perSession',
        ),
      },
      {
        value: KalturaRecordStatus.appended,
        label: this._appLocalization.get(
          'applications.upload.prepareLive.kalturaStreamType.enableRecordingOptions.appended',
        ),
      },
    ];
  }

  // Create empty structured form on loading
  private _createForm(): void {
    this._form = this._fb.group({
      name: ['', Validators.required],
      description: [''],
      transcodingProfile: [''],
      liveDVR: false,
      enableRecording: false,
      enableRecordingSelectedOption: [{ value: '', disabled: true }],
      previewMode: false,
    });

    this._form.valueChanges.pipe(cancelOnDestroy(this)).subscribe((data) => {
      this.dataChange.emit(data);
    });
  }

  public _toggleRecordingSelectedOption(enable: boolean) {
    enable
      ? this._form.get('enableRecordingSelectedOption').enable()
      : this._form.get('enableRecordingSelectedOption').disable();
  }

  private markFormFieldsAsTouched(): void {
    for (const inner in this._form.controls) {
      this._form.get(inner).markAsTouched();
      this._form.get(inner).updateValueAndValidity();
    }
  }

  private _updateAreaBlockerState(isBusy: boolean, message: AreaBlockerMessage): void {
    this._isBusy = isBusy;
    this._blockerMessage = message;
  }
}
